import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAppContext } from '../contexts/AppContext';
import { useNavigate, useLocation } from 'react-router-dom';
import MobileLayout from './MobileLayout';
import { 
  X, LogOut, User, QrCode, Calendar, Activity, Users, BarChart3, 
  ChevronDown, ChevronRight, ChevronLeft, ChevronUp, CalendarDays, LayoutGrid, 
  UserPlus, CreditCard, TrendingUp, Terminal, Settings, CalendarPlus, DollarSign, 
  Building2, Landmark, Stethoscope, ShieldCheck, Laptop, Pencil, Search, 
  FolderKanban, Tv, Sparkles, Award, CheckCircle2, ArrowRight, Layers, Clock, Calculator
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import logo from '../assets/logo-imaginem-radiologia.jpg';
import { apiFetch } from '../lib/apiClient';
import { fetchRangeEventsCached, fetchYearEventsCached, fetchAllYearsEventsCached } from '../lib/monthlyProjections';
import { hasModuleAccess, hasSubmoduleAccess, hasSocioPermission, canAccessReporteMedicos } from '../lib/permissions';
import FloatingTicketNotifications from './tickets/FloatingTicketNotifications';

const MODULE_THEMES = {
  portal: {
    bg: 'bg-indigo-50/70',
    iconBg: 'bg-indigo-100 text-indigo-700',
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    border: 'border-slate-200/80',
    hoverBorder: 'hover:border-indigo-300',
    accentText: 'text-indigo-600',
  },
  agendamiento: {
    bg: 'bg-violet-50/70',
    iconBg: 'bg-violet-100 text-violet-700',
    badge: 'bg-violet-50 text-violet-700 border-violet-200',
    border: 'border-slate-200/80',
    hoverBorder: 'hover:border-violet-300',
    accentText: 'text-violet-600',
  },
  recepcion: {
    bg: 'bg-blue-50/70',
    iconBg: 'bg-blue-100 text-blue-700',
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    border: 'border-slate-200/80',
    hoverBorder: 'hover:border-blue-300',
    accentText: 'text-blue-600',
  },
  operaciones: {
    bg: 'bg-amber-50/70',
    iconBg: 'bg-amber-100 text-amber-800',
    badge: 'bg-amber-50 text-amber-800 border-amber-200',
    border: 'border-slate-200/80',
    hoverBorder: 'hover:border-amber-300',
    accentText: 'text-amber-700',
  },
  finance: {
    bg: 'bg-emerald-50/70',
    iconBg: 'bg-emerald-100 text-emerald-700',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    border: 'border-slate-200/80',
    hoverBorder: 'hover:border-emerald-300',
    accentText: 'text-emerald-600',
  },
  hr: {
    bg: 'bg-teal-50/70',
    iconBg: 'bg-teal-100 text-teal-700',
    badge: 'bg-teal-50 text-teal-700 border-teal-200',
    border: 'border-slate-200/80',
    hoverBorder: 'hover:border-teal-300',
    accentText: 'text-teal-600',
  },
  crm: {
    bg: 'bg-sky-50/70',
    iconBg: 'bg-sky-100 text-sky-700',
    badge: 'bg-sky-50 text-sky-700 border-sky-200',
    border: 'border-slate-200/80',
    hoverBorder: 'hover:border-sky-300',
    accentText: 'text-sky-600',
  },
  marketing: {
    bg: 'bg-purple-50/70',
    iconBg: 'bg-purple-100 text-purple-700',
    badge: 'bg-purple-50 text-purple-700 border-purple-200',
    border: 'border-slate-200/80',
    hoverBorder: 'hover:border-purple-300',
    accentText: 'text-purple-600',
  },
  affiliate_marketing: {
    bg: 'bg-fuchsia-50/70',
    iconBg: 'bg-fuchsia-100 text-fuchsia-700',
    badge: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
    border: 'border-slate-200/80',
    hoverBorder: 'hover:border-fuchsia-300',
    accentText: 'text-fuchsia-600',
  },
  validador: {
    bg: 'bg-rose-50/70',
    iconBg: 'bg-rose-100 text-rose-700',
    badge: 'bg-rose-50 text-rose-700 border-rose-200',
    border: 'border-slate-200/80',
    hoverBorder: 'hover:border-rose-300',
    accentText: 'text-rose-600',
  },
  tv_turnos: {
    bg: 'bg-cyan-50/70',
    iconBg: 'bg-cyan-100 text-cyan-700',
    badge: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    border: 'border-slate-200/80',
    hoverBorder: 'hover:border-cyan-300',
    accentText: 'text-cyan-600',
  },
  dev: {
    bg: 'bg-slate-100/70',
    iconBg: 'bg-slate-200 text-slate-700',
    badge: 'bg-slate-100 text-slate-800 border-slate-300',
    border: 'border-slate-200/80',
    hoverBorder: 'hover:border-slate-400',
    accentText: 'text-slate-700',
  },
  config_admin: {
    bg: 'bg-zinc-100/70',
    iconBg: 'bg-zinc-200 text-zinc-700',
    badge: 'bg-zinc-100 text-zinc-800 border-zinc-300',
    border: 'border-slate-200/80',
    hoverBorder: 'hover:border-zinc-400',
    accentText: 'text-zinc-700',
  },
  tickets: {
    bg: 'bg-indigo-50/70',
    iconBg: 'bg-indigo-100 text-indigo-700',
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    border: 'border-slate-200/80',
    hoverBorder: 'hover:border-indigo-300',
    accentText: 'text-indigo-600',
  },
};

export default function MainLayout({ children }) {
  const { user, logout } = useAuth();
  const { activeTab, setActiveTab, subOptions, globalStats, isMobile } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Launcher modal state
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  // Tooltip state { x, y, title, subtitle, badge }
  const [activeTooltip, setActiveTooltip] = useState(null);

  // QR Tab state
  const [qrTab, setQrTab] = useState('mobile');
  const [pairingUrls, setPairingUrls] = useState({});
  // Collapsible sidebar state (hidden as a notch by default)
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Collapsible QR codes panel in left sidebar
  const [qrSectionOpen, setQrSectionOpen] = useState(false);

  const [stationName, setStationName] = useState(() => localStorage.getItem('station_name') || '');
  const [isEditingStationName, setIsEditingStationName] = useState(false);

  // Search in mega menu
  const [menuSearchQuery, setMenuSearchQuery] = useState('');
  const searchInputRef = useRef(null);

  const showTooltip = (e, { title, subtitle, badge }) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setActiveTooltip({
      x: rect.right + 10,
      y: rect.top + rect.height / 2,
      title,
      subtitle,
      badge,
    });
  };

  const hideTooltip = () => {
    setActiveTooltip(null);
  };

  const openModuleLauncher = () => {
    hideTooltip();
    setMenuSearchQuery('');
    setMegaMenuOpen(true);
  };

  const closeModuleLauncher = () => {
    setMegaMenuOpen(false);
    setMenuSearchQuery('');
  };

  useEffect(() => {
    if (megaMenuOpen) {
      const timer = setTimeout(() => searchInputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [megaMenuOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeModuleLauncher();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const handleScroll = () => setActiveTooltip(null);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  useEffect(() => {
    if (globalStats?.stationId) {
      const stored = localStorage.getItem('station_name');
      if (stored) {
        requestAnimationFrame(() => setStationName(stored));
      }
    }
  }, [globalStats?.stationId]);

  const handleSaveStationName = async (val) => {
    const trimmed = val.trim();
    setStationName(trimmed);
    localStorage.setItem('station_name', trimmed);
    setIsEditingStationName(false);
    if (globalStats?.stationId && trimmed) {
      try {
        await apiFetch(`/api/mobile/station/${globalStats.stationId}/name`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ station_name: trimmed })
        });
      } catch (e) {
        console.error('Failed to sync station name:', e);
      }
    }
  };

  useEffect(() => {
    if (!globalStats?.stationId || !qrSectionOpen) return;
    
    let cancelled = false;
    const fetchPairingUrls = async () => {
      const issue = async (deviceType) => {
        const response = await apiFetch('/api/mobile/pairing', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ station_id: globalStats.stationId, device_type: deviceType })
        });
        if (!response.ok) throw new Error('pairing_failed');
        return (await response.json()).url;
      };
      
      try {
        const [mobile, ipad] = await Promise.all([issue('scan'), issue('consent')]);
        if (!cancelled) setPairingUrls({ mobile, ipad });
      } catch {
        if (!cancelled) setPairingUrls({ error: true });
      }
    };

    fetchPairingUrls();
    
    // Refresh the tokens every 9 minutes (they expire in 10) so they don't expire if left open
    const interval = setInterval(fetchPairingUrls, 9 * 60 * 1000);

    return () => { 
      cancelled = true; 
      clearInterval(interval);
    };
  }, [globalStats?.stationId, qrSectionOpen, pairingUrls.error]);

  // If mobile, delegate entirely to MobileLayout
  if (isMobile) {
    return <MobileLayout>{children}</MobileLayout>;
  }

  // Comprehensive modules definition categorized for ultra high UX/UI usability
  const modulesMenu = [
    // --- CATEGORÍA 1: ATENCIÓN MÉDICA Y OPERACIONES ---
    {
      category: 'Atención Médica y Operaciones',
      moduleId: 'portal',
      title: 'Portal Principal',
      path: '/',
      icon: <Activity className="w-5 h-5 text-indigo-600" />,
      roles: ['admin', 'recepcion', 'patient_care', 'hr', 'dev', 'crm', 'marketing', 'medico', 'tecnico'],
      description: 'Tablero resumen con indicadores clave y accesos directos.',
      subOptions: []
    },
    {
      category: 'Atención Médica y Operaciones',
      moduleId: 'agendamiento',
      title: 'Agendamiento y Atención al Paciente',
      path: '/agendamiento',
      icon: <CalendarPlus className="w-5 h-5 text-violet-600" />,
      roles: ['admin', 'agendamiento', 'patient_care'],
      description: 'Agendamiento manual, calendario, Kommo leads y llamadas BI.',
      subOptions: [
        { id: 'manual', label: 'Agendamiento Manual', icon: <UserPlus className="w-3 h-3" /> },
        { id: 'calendario', label: 'Vista Calendario', icon: <CalendarDays className="w-3 h-3" /> },
        { id: 'leads', label: 'Leads Kommo', icon: <LayoutGrid className="w-3 h-3" /> },
        { id: 'bi', label: 'Análisis de Llamadas', icon: <BarChart3 className="w-3 h-3" /> },
        { id: 'analisis_agendamiento', label: 'Análisis de Citas', icon: <TrendingUp className="w-3 h-3" /> },
        { id: 'productividad', label: 'Productividad ATC', icon: <Award className="w-3 h-3" /> },
        { id: 'cotizacion', label: 'Cotización', icon: <Calculator className="w-3 h-3" /> }
      ]
    },
    {
      category: 'Atención Médica y Operaciones',
      moduleId: 'recepcion',
      title: 'Recepción y Flujo de Pacientes',
      path: '/finanzas/recepcion',
      icon: <Calendar className="w-5 h-5 text-blue-600" />,
      roles: ['admin', 'recepcion', 'patient_care', 'operaciones'],
      description: 'Recepción, agendas, Kanban, cortes del día y revisión médica.',
      subOptions: [
        { id: 'recepcion', label: 'Vista Lista', icon: <UserPlus className="w-3 h-3" /> },
        { id: 'vistacalendario', label: 'Vista Calendario', icon: <Calendar className="w-3 h-3" /> },
        { id: 'kanban', label: 'Vista Kanban', icon: <LayoutGrid className="w-3 h-3" /> },
        { id: 'corte', label: 'Corte del Día', icon: <CreditCard className="w-3 h-3" /> },
        { id: 'revision_medicos', label: 'Revisión Médicos', icon: <Stethoscope className="w-3 h-3" /> },
        { id: 'reporte_medicos', label: 'Reporte por Médicos', icon: <TrendingUp className="w-3 h-3" /> },
        { id: 'graficas', label: 'Gráficas', icon: <TrendingUp className="w-3 h-3" /> },
        { id: 'semanal', label: 'Vista Semanal', icon: <CalendarDays className="w-3 h-3" /> },
        { id: 'mensual', label: 'Vista Mensual', icon: <CalendarDays className="w-3 h-3" /> },
        { id: 'anual', label: 'Vista Anual', icon: <BarChart3 className="w-3 h-3" /> },
        { id: 'multianual', label: 'Vista Multianual', icon: <BarChart3 className="w-3 h-3" /> }
      ]
    },
    {
      category: 'Atención Médica y Operaciones',
      moduleId: 'operaciones',
      title: 'Operaciones Médicas',
      path: '/operaciones',
      icon: <Activity className="w-5 h-5 text-amber-600" />,
      roles: ['admin', 'operaciones', 'patient_care', 'tecnico', 'medico', 'sala_estudios', 'dev'],
      description: 'Agenda técnica operativa, verificación de estudios y salas.',
      subOptions: [
        { id: 'agenda', label: 'Agenda Técnica', path: '/operaciones?tab=agenda', icon: <Calendar className="w-3 h-3" /> },
        { id: 'verificacion', label: 'Verificación Técnica', path: '/operaciones?tab=verificacion', icon: <CheckCircle2 className="w-3 h-3" /> },
        { id: 'salas', label: 'Sala de Estudios', path: '/operaciones?tab=salas', icon: <LayoutGrid className="w-3 h-3" /> },
        { id: 'timeline', label: 'Timeline de Salas', path: '/operaciones?tab=timeline', icon: <Clock className="w-3 h-3" /> }
      ]
    },

    // --- CATEGORÍA 2: GESTIÓN EMPRESARIAL Y COMERCIAL ---
    {
      category: 'Gestión Empresarial y Comercial',
      moduleId: 'finance',
      title: 'Finanzas Corporativas',
      path: '/finanzas/corporativas',
      icon: <DollarSign className="w-5 h-5 text-emerald-600" />,
      roles: ['admin', 'finance'],
      description: 'Estados de cuenta, ingresos, gastos, flujo de caja unificado, préstamos y estados financieros.',
      subOptions: [
        { id: 'estados_cuenta', label: 'Estados de Cuenta', icon: <FolderKanban className="w-3 h-3" /> },
        { id: 'ingresos', label: 'Ingresos', icon: <TrendingUp className="w-3 h-3" /> },
        { id: 'gastos', label: 'Gastos', icon: <CreditCard className="w-3 h-3" /> },
        { id: 'flujo_caja', label: 'Flujo de Caja', icon: <Layers className="w-3 h-3" /> },
        { id: 'prestamos', label: 'Préstamos a Devolver', icon: <Landmark className="w-3 h-3" /> },
        { id: 'proveedores', label: 'Proveedores', icon: <Building2 className="w-3 h-3" /> },
        { id: 'estados_financieros', label: 'Estados Financieros', icon: <BarChart3 className="w-3 h-3" /> },
        { id: 'cuentas_pagar', label: 'Cuentas por Pagar', icon: <CreditCard className="w-3 h-3" /> },
        { id: 'cuentas_cobrar', label: 'Cuentas por Cobrar', icon: <CalendarDays className="w-3 h-3" /> }
      ]
    },
    {
      category: 'Gestión Empresarial y Comercial',
      moduleId: 'hr',
      title: 'Recursos Humanos',
      path: '/hr',
      icon: <Users className="w-5 h-5 text-emerald-600" />,
      roles: ['admin', 'hr', 'medico'],
      description: 'Control de personal, directorio de empleados y checador.',
      subOptions: [
        { id: 'rh_asistencia', label: 'Control de Asistencia', path: '/hr', icon: <Activity className="w-3 h-3" /> },
        { id: 'rh_empleados', label: 'Gestionar Empleados', path: '/hr/empleados', icon: <Users className="w-3 h-3" /> },
        { id: 'rh_checador', label: 'Checador Tablet', path: '/checador', icon: <Tv className="w-3 h-3" /> },
        { id: 'rh_externos', label: 'Usuarios Externos', path: '/hr/externos', icon: <UserPlus className="w-3 h-3" /> }
      ]
    },
    {
      category: 'Gestión Empresarial y Comercial',
      moduleId: 'crm',
      title: 'CRM Médicos y Pacientes',
      path: '/crm',
      icon: <Users className="w-5 h-5 text-sky-600" />,
      roles: ['admin', 'crm', 'canvaseador', 'closer', 'patient_care'],
      description: 'Médicos referentes, contactos comerciales y CRM de pacientes.',
      subOptions: [
        { id: 'crm_medicos', label: 'Pipeline Médicos', path: '/crm', icon: <Stethoscope className="w-3 h-3" /> },
        { id: 'crm_directorio', label: 'Médicos Referentes', path: '/erp/medicos/contactos', icon: <Users className="w-3 h-3" /> },
        { id: 'crm_pacientes', label: 'CRM Pacientes', path: '/crm-pacientes', icon: <Activity className="w-3 h-3" /> }
      ]
    },
    {
      category: 'Gestión Empresarial y Comercial',
      moduleId: 'marketing',
      title: 'Marketing Masivo',
      path: '/marketing',
      icon: <BarChart3 className="w-5 h-5 text-purple-600" />,
      roles: ['admin', 'marketing'],
      description: 'Envíos masivos y campañas para pacientes y médicos.',
      subOptions: []
    },
    {
      category: 'Gestión Empresarial y Comercial',
      moduleId: 'tickets',
      title: 'Tickets Internos y Tareas',
      path: '/tickets',
      icon: <Layers className="w-5 h-5 text-indigo-600" />,
      roles: ['admin', 'operaciones', 'hr', 'recepcion', 'finance', 'patient_care', 'dev', 'medico', 'tecnico', 'agendamiento', 'crm', 'marketing', 'empleado'],
      description: 'Gestión corporativa de tareas, peticiones, deadlines y seguimiento.',
      subOptions: []
    },
    {
      category: 'Gestión Empresarial y Comercial',
      moduleId: 'affiliate_marketing',
      title: 'Affiliate Marketing Platform',
      path: '/affiliate/agendamiento',
      icon: <Sparkles className="w-5 h-5 text-indigo-600" />,
      roles: ['admin', 'marketing', 'affiliate'],
      description: 'Plataforma para afiliados de marketing, comisiones y agendamiento directo.',
      subOptions: [
        { id: 'affiliate.agendamiento', label: 'Agendamiento', path: '/affiliate/agendamiento', icon: <Calendar className="w-3 h-3" /> },
        { id: 'affiliate.facturacion', label: 'Facturación', path: '/affiliate/facturacion', icon: <CreditCard className="w-3 h-3" /> },
        { id: 'affiliate.configuracion', label: 'Configuración', path: '/affiliate/configuracion', icon: <Settings className="w-3 h-3" /> }
      ]
    },

    // --- CATEGORÍA 3: HERRAMIENTAS Y CONFIGURACIÓN ---
    {
      category: 'Herramientas y Configuración',
      moduleId: 'validador',
      title: 'Validador de Documentos',
      path: '/validador',
      icon: <ShieldCheck className="w-5 h-5 text-indigo-600" />,
      roles: ['admin', 'validador', 'medico', 'recepcion', 'patient_care', 'hr', 'dev'],
      description: 'Validación de códigos QR y autenticidad de recibos / estudios.',
      subOptions: []
    },
    {
      category: 'Herramientas y Configuración',
      moduleId: 'tv_turnos',
      title: 'Pantalla TV Turnos',
      path: '/tv/turnos',
      icon: <Tv className="w-5 h-5 text-cyan-600" />,
      roles: ['admin', 'tv_turnos', 'recepcion', 'patient_care', 'dev'],
      description: 'Vista de turnos para pantallas de sala de espera.',
      subOptions: []
    },
    {
      category: 'Herramientas y Configuración',
      moduleId: 'dev',
      title: 'Herramientas de Desarrollador',
      path: '/developer',
      icon: <Terminal className="w-5 h-5 text-indigo-500" />,
      roles: ['admin', 'dev'],
      description: 'Diagnósticos del sistema, importadores de datos y simuladores.',
      subOptions: [
        { id: 'dev_tools', label: 'Herramientas Dev', path: '/developer', icon: <Terminal className="w-3 h-3" /> },
        { id: 'dev_import', label: 'Importador CSV Batch', path: '/dev/importador', icon: <FolderKanban className="w-3 h-3" /> },
        { id: 'dev_sim', label: 'Simulador Integraciones', path: '/dev/simulador', icon: <Activity className="w-3 h-3" /> },
        { id: 'dev_json', label: 'Importador JSON', path: '/import-json', icon: <FolderKanban className="w-3 h-3" /> },
        { id: 'dev_sys', label: 'System Process Check', path: '/system/processes', icon: <Activity className="w-3 h-3" /> }
      ]
    },
    {
      category: 'Herramientas y Configuración',
      moduleId: 'config_admin',
      title: 'Configuración Global',
      path: '/configuracion',
      icon: <Settings className="w-5 h-5 text-slate-700" />,
      roles: ['admin', 'config_admin'],
      description: 'Parámetros del sistema, llaves de API e impresoras QZ.',
      subOptions: []
    }
  ];

  const userRoles = Array.isArray(user?.roles) ? user.roles : [];
  const accessibleModules = modulesMenu.filter(m => {
    if (!user) return false;
    if (userRoles.includes('admin') || hasSocioPermission(user)) return true;
    if (!Array.isArray(user.roles)) return false;
    if (m.moduleId && m.moduleId !== 'portal' && hasModuleAccess(user, m.moduleId)) return true;
    return Array.isArray(m.roles) && m.roles.some(r => userRoles.includes(r));
  }).map(mod => {
    if (!mod.moduleId || mod.moduleId === 'portal' || !user || userRoles.includes('admin') || hasSocioPermission(user)) {
      return mod;
    }
    const filteredSubs = (mod.subOptions || []).filter(sub => 
      hasSubmoduleAccess(user, mod.moduleId, sub.id)
    );
    return { ...mod, subOptions: filteredSubs };
  });

  const normMenuStr = (str) => String(str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const queryClean = normMenuStr(menuSearchQuery).trim();

  const filteredAccessibleModules = accessibleModules.map(mod => {
    if (!queryClean) return { ...mod, filteredSubOptions: mod.subOptions };
    const titleMatch = normMenuStr(mod.title).includes(queryClean);
    const descMatch = normMenuStr(mod.description).includes(queryClean);
    const categoryMatch = normMenuStr(mod.category).includes(queryClean);
    const matchedSubOptions = (mod.subOptions || []).filter(sub => 
      normMenuStr(sub.label).includes(queryClean)
    );

    if (titleMatch || descMatch || categoryMatch || matchedSubOptions.length > 0) {
      return {
        ...mod,
        filteredSubOptions: (titleMatch || descMatch || categoryMatch) ? mod.subOptions : matchedSubOptions
      };
    }
    return null;
  }).filter(Boolean);

  const categoryCategories = [
    { id: 'Atención Médica y Operaciones', label: 'Atención Médica y Operaciones', shortLabel: 'Atención Médica' },
    { id: 'Gestión Empresarial y Comercial', label: 'Gestión Empresarial y Comercial', shortLabel: 'Gestión Empresarial' },
    { id: 'Herramientas y Configuración', label: 'Herramientas y Configuración', shortLabel: 'Herramientas' },
  ];
  const categoryTabs = categoryCategories.map(cat => ({
    ...cat,
    count: accessibleModules.filter(m => m.category === cat.id).length
  })).filter(cat => cat.count > 0);

  const displayedModules = filteredAccessibleModules.filter(mod => {
    if (selectedCategoryFilter === 'all') return true;
    return mod.category === selectedCategoryFilter;
  });

  const handleNavigateModule = (e, targetPath) => {
    if (e.metaKey || e.ctrlKey) {
      window.open(targetPath, '_blank');
      return;
    }
    closeModuleLauncher();
    navigate(targetPath);
  };

  const handleNavigateSub = (e, modulePath, sub) => {
    const targetUrl = sub.path ? sub.path : `${modulePath}?tab=${sub.id}`;
    if (e.metaKey || e.ctrlKey) {
      window.open(targetUrl, '_blank');
      return;
    }
    closeModuleLauncher();
    if (sub.path) {
      navigate(sub.path);
    } else {
      navigate(targetUrl);
      setActiveTab(sub.id);
    }
  };

  const hasRecepcionAccess = user && (hasSocioPermission(user) || canAccessReporteMedicos(user) || (Array.isArray(user.roles) && (userRoles.includes('admin') || userRoles.includes('recepcion') || userRoles.includes('patient_care') || userRoles.includes('operaciones'))));

  // Determine active sub-options to show in the left collapsible sidebar
  const currentModuleObj = accessibleModules.find(m => 
    m.path !== '/' && (location.pathname === m.path || location.pathname.startsWith(m.path + '/'))
  ) || accessibleModules.find(m => 
    Array.isArray(m.subOptions) && m.subOptions.some(sub => {
      if (!sub.path) return false;
      const subBasePath = sub.path.split('?')[0];
      return subBasePath !== '/' && (location.pathname === subBasePath || location.pathname.startsWith(subBasePath + '/'));
    })
  ) || accessibleModules.find(m => {
    if (m.moduleId === 'affiliate_marketing' && location.pathname.startsWith('/affiliate')) return true;
    return false;
  }) || accessibleModules.find(m => m.path === '/') || accessibleModules[0];
  const rawSubOptions = subOptions.length > 0 ? subOptions : (currentModuleObj?.subOptions || []);
  const activeSubOptions = rawSubOptions.filter(sub => {
    if (!currentModuleObj?.moduleId || currentModuleObj.moduleId === 'portal' || !user || userRoles.includes('admin') || hasSocioPermission(user)) {
      return true;
    }
    return hasSubmoduleAccess(user, currentModuleObj.moduleId, sub.id);
  });

  const formatCurrency = (val) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val || 0);

  // Layout exclusivo para Afiliados de Marketing (Aislamiento Total de Módulos Internos)
  if (user?.user_type === 'affiliate') {
    return (
      <div className="flex flex-col h-screen w-full bg-slate-50 overflow-hidden font-sans">
        {/* Cabecera Exclusiva de Plataforma de Afiliados */}
        <header className="bg-white border-b border-slate-200 px-6 py-3.5 flex items-center justify-between shadow-xs shrink-0 z-50">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <img src={logo} alt="Imaginem Radiología" className="w-9 h-9 object-contain rounded-xl shadow-xs" />
              <div>
                <div className="text-sm font-black text-slate-900 tracking-tight leading-none">
                  Affiliate Marketing Platform
                </div>
                <div className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mt-0.5">
                  Imaginem Radiología
                </div>
              </div>
            </div>

            <div className="h-6 w-px bg-slate-200 hidden sm:block" />

            {/* Navegación de Submódulos de Afiliados */}
            <nav className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/affiliate/agendamiento')}
                className={`px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-2 ${
                  location.pathname.startsWith('/affiliate/agendamiento')
                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20'
                    : 'text-slate-600 hover:text-indigo-600 hover:bg-slate-100'
                }`}
              >
                <Calendar className="w-4 h-4" />
                <span>Agendamiento</span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/affiliate/facturacion')}
                className={`px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-2 ${
                  location.pathname.startsWith('/affiliate/facturacion')
                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20'
                    : 'text-slate-600 hover:text-indigo-600 hover:bg-slate-100'
                }`}
              >
                <CreditCard className="w-4 h-4" />
                <span>Facturación</span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/affiliate/configuracion')}
                className={`px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-2 ${
                  location.pathname.startsWith('/affiliate/configuracion')
                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20'
                    : 'text-slate-600 hover:text-indigo-600 hover:bg-slate-100'
                }`}
              >
                <Settings className="w-4 h-4" />
                <span>Configuración</span>
              </button>
            </nav>
          </div>

          {/* Perfil del Afiliado y Salida */}
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <div className="text-xs font-black text-slate-800">
                {user.nombre || user.email}
              </div>
              <div className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">
                Socio Afiliado Autorizado
              </div>
            </div>

            <button
              type="button"
              onClick={logout}
              className="p-2.5 rounded-xl text-slate-500 hover:text-rose-600 hover:bg-rose-50 border border-slate-200 transition-colors flex items-center gap-1.5 text-xs font-bold"
              title="Cerrar Sesión"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden md:inline">Salir</span>
            </button>
          </div>
        </header>

        {/* Contenido Principal de Afiliados */}
        <main className="flex-1 overflow-y-auto bg-slate-50">
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen w-full bg-slate-50 overflow-hidden font-sans">
      
      {/* CUERPO PRINCIPAL CON MENÚ LATERAL COLAPSABLE PERMANENTE Y CONTENIDO */}
      <div className="flex-1 flex overflow-hidden relative bg-slate-50 min-h-0 min-w-0">
        
        {/* MENÚ LATERAL COLAPSABLE (A la izquierda) PERMANENTE */}
        <aside 
          className={`flex flex-col bg-white border-r border-slate-200 shadow-md shrink-0 transition-all duration-300 ease-in-out z-[150] relative ${
            sidebarOpen ? 'w-72' : 'w-16'
          }`}
        >
          {/* Muesca / Flechita para abrir/cerrar */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="absolute -right-3.5 top-5 w-7 h-10 bg-white border border-slate-300 rounded-r-xl shadow-xl flex items-center justify-center text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 transition-all z-[200] cursor-pointer"
            title={sidebarOpen ? "Ocultar menú" : "Mostrar menú"}
          >
            {sidebarOpen ? <ChevronLeft className="w-4 h-4 stroke-[2.5]" /> : <ChevronRight className="w-4 h-4 stroke-[2.5]" />}
          </button>

          {/* TOP SECTION: Logo & Mega Menu */}
          <div className="flex flex-col gap-4 p-3 border-b border-slate-100 z-50">
            {/* Logo */}
            <div className={`flex items-center ${sidebarOpen ? 'justify-start px-2' : 'justify-center'} overflow-hidden h-10 mt-2`}>
              <img src={logo} alt="Imaginem" className="w-8 h-8 object-contain shrink-0 rounded-lg shadow-sm" />
              {sidebarOpen && (
                <div className="ml-3 flex flex-col">
                  <span className="text-[13px] font-black text-slate-800 tracking-tight leading-tight">GEMA ERP</span>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Imaginem</span>
                </div>
              )}
            </div>
            
            {/* BOTÓN UNIFICADO: Selector y Visualizador del Módulo Actual */}
            {sidebarOpen ? (
              <button
                type="button"
                onClick={() => {
                  if (megaMenuOpen) {
                    closeModuleLauncher();
                  } else {
                    openModuleLauncher();
                  }
                }}
                className={`w-full flex items-center justify-between p-2.5 rounded-xl border transition-all duration-200 text-left group cursor-pointer ${
                  megaMenuOpen 
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-600/25 ring-2 ring-indigo-400/30' 
                    : 'bg-white hover:bg-slate-50/90 border-slate-200/90 hover:border-indigo-300 text-slate-800 shadow-xs hover:shadow-sm'
                }`}
                aria-label={`Módulo actual: ${currentModuleObj?.title || 'Selector de Módulos'}. Clic para cambiar.`}
              >
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  <div className={`p-2 rounded-lg shrink-0 transition-colors shadow-2xs ${
                    megaMenuOpen 
                      ? 'bg-white/20 text-white' 
                      : 'bg-indigo-50 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white'
                  }`}>
                    {currentModuleObj?.icon 
                      ? React.cloneElement(currentModuleObj.icon, { className: "w-4 h-4" }) 
                      : <LayoutGrid className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className={`text-[9px] font-extrabold uppercase tracking-widest block leading-tight ${
                      megaMenuOpen ? 'text-indigo-100' : 'text-indigo-600'
                    }`}>
                      Módulo Actual
                    </span>
                    <span className={`text-xs font-black truncate block leading-snug mt-0.5 ${
                      megaMenuOpen ? 'text-white' : 'text-slate-800 group-hover:text-indigo-600'
                    }`}>
                      {currentModuleObj?.title || 'Módulos del Sistema'}
                    </span>
                  </div>
                </div>
                <div className={`pl-2 shrink-0 transition-transform duration-200 ${
                  megaMenuOpen ? 'rotate-180 text-white' : 'text-slate-400 group-hover:text-indigo-600'
                }`}>
                  <ChevronDown className="w-4 h-4" />
                </div>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (megaMenuOpen) {
                    closeModuleLauncher();
                  } else {
                    openModuleLauncher();
                  }
                }}
                onMouseEnter={(e) => showTooltip(e, {
                  title: currentModuleObj?.title || 'Módulos del Sistema',
                  subtitle: 'Clic para cambiar de módulo',
                  badge: 'Módulo Actual'
                })}
                onMouseLeave={hideTooltip}
                className={`w-10 h-10 mx-auto flex items-center justify-center rounded-xl border transition-all duration-200 group cursor-pointer ${
                  megaMenuOpen
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-600/30 ring-2 ring-indigo-400/30'
                    : 'bg-white hover:bg-indigo-50 border-slate-200/90 hover:border-indigo-300 text-indigo-600 shadow-2xs hover:shadow-xs'
                }`}
                aria-label={`Módulo actual: ${currentModuleObj?.title || 'Selector de Módulos'}`}
              >
                {currentModuleObj?.icon 
                  ? React.cloneElement(currentModuleObj.icon, { className: "w-5 h-5" }) 
                  : <LayoutGrid className="w-5 h-5" />}
              </button>
            )}
          </div>

          {/* MIDDLE SECTION: Suboptions */}
          <div className="flex-1 overflow-y-auto custom-scrollbar p-3">
            {Array.isArray(activeSubOptions) && activeSubOptions.length > 0 ? (
              <>
                {sidebarOpen && <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2.5 px-2">Subopciones</p>}
                <div className="flex flex-col gap-1.5">
                  {activeSubOptions.map((opt, oidx) => {
                    const currentTabParam = new URLSearchParams(location.search).get('tab');
                    const isActive = opt.path 
                      ? (opt.path.includes('?') 
                          ? location.pathname === opt.path.split('?')[0] && location.search.includes(opt.path.split('?')[1])
                          : location.pathname === opt.path)
                      : (currentTabParam ? currentTabParam === opt.id : activeTab === opt.id);
                    return (
                      <button
                        key={oidx}
                        type="button"
                        title={opt.label}
                        onClick={(e) => {
                          hideTooltip();
                          const currentParams = new URLSearchParams(location.search);
                          currentParams.set('tab', opt.id);
                          currentParams.delete('eventId');
                          const targetUrl = opt.path ? opt.path : `${currentModuleObj?.path || location.pathname}?${currentParams.toString()}`;
                          if (e.metaKey || e.ctrlKey) {
                            window.open(targetUrl, '_blank');
                            return;
                          }
                          navigate(targetUrl);
                          setActiveTab(opt.id);
                        }}
                        onMouseEnter={(e) => {
                          if (opt.id === 'semanal') {
                            const now = new Date();
                            const pad = n => String(n).padStart(2, '0');
                            const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                            const endDay = endOfMonth.getDay();
                            const diffToSunday = endDay === 0 ? 0 : 7 - endDay;
                            endOfMonth.setDate(endOfMonth.getDate() + diffToSunday);
                            const startOfRolling = new Date(endOfMonth);
                            startOfRolling.setDate(startOfRolling.getDate() - (12 * 7 - 1));
                            const todayStr = new Intl.DateTimeFormat('en-CA', {
                              timeZone: 'America/Tijuana',
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit'
                            }).format(now);
                            const [ty, tm, td] = todayStr.split('-').map(Number);
                            const twelveWeeksAgo = new Date(ty, tm - 1, td - 84);
                            const minHistStart = `${twelveWeeksAgo.getFullYear()}-${pad(twelveWeeksAgo.getMonth() + 1)}-${pad(twelveWeeksAgo.getDate())}`;
                            const rangeStart = `${startOfRolling.getFullYear()}-${pad(startOfRolling.getMonth() + 1)}-${pad(startOfRolling.getDate())}`;
                            const rangeEnd = `${endOfMonth.getFullYear()}-${pad(endOfMonth.getMonth() + 1)}-${pad(endOfMonth.getDate())}`;
                            const fetchStart = (rangeStart <= todayStr && rangeEnd >= todayStr && minHistStart < rangeStart) ? minHistStart : rangeStart;
                            fetchRangeEventsCached(fetchStart, rangeEnd, apiFetch).catch(() => {});
                          } else if (opt.id === 'mensual') {
                            const now = new Date();
                            const pad = n => String(n).padStart(2, '0');
                            const endObj = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                            const startObj = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29);
                            const startStr = `${startObj.getFullYear()}-${pad(startObj.getMonth() + 1)}-${pad(startObj.getDate())}`;
                            const endStr = `${endObj.getFullYear()}-${pad(endObj.getMonth() + 1)}-${pad(endObj.getDate())}`;
                            fetchRangeEventsCached(startStr, endStr, apiFetch).catch(() => {});
                          } else if (opt.id === 'anual') {
                            const yr = new Date().getFullYear();
                            const targetYr = yr >= 2026 ? String(yr) : '2026';
                            fetchYearEventsCached(targetYr, apiFetch).catch(() => {});
                          } else if (opt.id === 'multianual') {
                            fetchAllYearsEventsCached(apiFetch).catch(() => {});
                          }
                          if (!sidebarOpen) {
                            showTooltip(e, {
                              title: opt.label,
                              badge: isActive ? 'Activo' : undefined
                            });
                          }
                        }}
                        onMouseLeave={hideTooltip}
                        className={`flex items-center ${sidebarOpen ? 'gap-2.5 px-3' : 'justify-center'} py-2.5 rounded-xl text-xs font-bold transition-all duration-200 text-left cursor-pointer ${
                          isActive 
                            ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/20 font-black' 
                            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                        }`}
                      >
                        {opt.icon && <span className={isActive ? 'text-white' : 'text-slate-500'}>{opt.icon}</span>}
                        {sidebarOpen && <span className="truncate">{opt.label}</span>}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              sidebarOpen && (
                <div className="h-full flex items-center justify-center text-center px-4">
                  <p className="text-[10px] font-semibold text-slate-400">Selecciona un módulo superior para ver opciones.</p>
                </div>
              )
            )}
          </div>

          {/* BOTTOM SECTION: User Details, QRs, and Logout */}
          <div className="mt-auto border-t border-slate-100 bg-slate-50/50 p-3 flex flex-col gap-3 relative max-h-[50vh] overflow-y-auto custom-scrollbar">
            
            {/* User Profile Header (Click to expand sidebar if closed) */}
            <div 
              className={`flex items-center gap-3 ${!sidebarOpen ? 'justify-center cursor-pointer hover:bg-slate-200/50 p-1.5 rounded-lg' : ''}`}
              onClick={() => { if (!sidebarOpen) setSidebarOpen(true); }}
              onMouseEnter={(e) => {
                if (!sidebarOpen) {
                  showTooltip(e, {
                    title: user?.nombre || user?.email || 'Usuario',
                    subtitle: 'Clic para expandir menú',
                    badge: 'En línea'
                  });
                }
              }}
              onMouseLeave={hideTooltip}
            >
              <div className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-tr from-indigo-500 to-purple-600 p-[2px] shadow-sm shrink-0">
                <div className="w-full h-full rounded-full overflow-hidden bg-white flex items-center justify-center font-bold text-indigo-600 text-[11px]">
                  {user?.foto_perfil ? (
                    <img src={user.foto_perfil} alt={user.nombre || user.email} className="w-full h-full object-cover" />
                  ) : (
                    (user?.nombre || user?.email || 'U').charAt(0).toUpperCase()
                  )}
                </div>
              </div>
              
              {sidebarOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-black text-slate-800 truncate">{user?.nombre || user?.email}</p>
                  <p className="text-[9px] font-bold text-emerald-600 flex items-center gap-1 mt-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    En línea
                  </p>
                </div>
              )}
            </div>

            {sidebarOpen && (
              <div className="flex flex-col gap-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
                
                {/* Mi Perfil Link */}
                <button 
                  onClick={() => navigate('/perfil')}
                  className="w-full px-3 py-1.5 text-left text-[11px] font-bold text-slate-700 bg-white border border-slate-200 hover:border-indigo-300 hover:text-indigo-600 rounded-lg flex items-center justify-center gap-2 transition-colors shadow-sm"
                >
                  <User className="w-3.5 h-3.5" />
                  Mi Perfil
                </button>

                {/* Station Name Editor */}
                {hasRecepcionAccess && globalStats?.stationId && (
                  <div className="bg-white border border-slate-200 p-2 rounded-xl shadow-sm">
                    <div className="flex items-center justify-between text-[9px] font-bold text-slate-400 mb-1.5">
                      <span className="flex items-center gap-1 uppercase tracking-wider">
                        <Laptop className="w-3 h-3 text-indigo-500" /> Estación
                      </span>
                    </div>
                    {isEditingStationName ? (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          value={stationName}
                          onChange={(e) => setStationName(e.target.value)}
                          placeholder="Ej. Caja 2"
                          className="w-full text-[10px] font-bold text-slate-800 px-2 py-1 border border-indigo-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-slate-50"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveStationName(stationName);
                            if (e.key === 'Escape') {
                              setStationName(localStorage.getItem('station_name') || '');
                              setIsEditingStationName(false);
                            }
                          }}
                        />
                        <button
                          onClick={() => handleSaveStationName(stationName)}
                          className="px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-[10px] font-bold transition-colors shrink-0 shadow-xs cursor-pointer"
                        >
                          OK
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => setIsEditingStationName(true)}
                        className="flex items-center justify-between px-2 py-1 rounded bg-slate-50 border border-slate-200/50 hover:bg-indigo-50/50 hover:border-indigo-200 cursor-pointer transition-all"
                        title="Nombrar estación"
                      >
                        <span className="text-[10px] font-extrabold text-slate-700 truncate">
                          {stationName || 'Sin Nombre'}
                        </span>
                        <Pencil className="w-3 h-3 text-slate-400 shrink-0 ml-1.5" />
                      </div>
                    )}
                  </div>
                )}

                {/* QR Codes for Mobile/iPad pairing (Collapsible) */}
                {hasRecepcionAccess && globalStats?.stationId && (
                  <div className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden transition-all">
                    <button
                      onClick={() => setQrSectionOpen(!qrSectionOpen)}
                      className="w-full px-2.5 py-1.5 flex items-center justify-between text-[10px] font-extrabold text-slate-700 hover:text-indigo-600 hover:bg-slate-50 transition-colors cursor-pointer"
                    >
                      <span className="flex items-center gap-1.5 uppercase tracking-wider text-[9px] text-slate-500">
                        <QrCode className="w-3.5 h-3.5 text-indigo-600" /> Vinculación QR
                      </span>
                      {qrSectionOpen ? (
                        <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                      )}
                    </button>

                    {qrSectionOpen && (
                      <div className="p-2 border-t border-slate-100 flex flex-col items-center animate-in fade-in duration-200">
                        <div className="flex w-full mb-2 rounded bg-slate-100 p-0.5">
                          <button
                            onClick={() => setQrTab('mobile')}
                            className={`flex-1 py-1 text-[9px] font-bold rounded-sm transition-colors ${qrTab === 'mobile' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-500 hover:text-slate-700'}`}
                          >
                            Docs
                          </button>
                          <button
                            onClick={() => setQrTab('ipad')}
                            className={`flex-1 py-1 text-[9px] font-bold rounded-sm transition-colors ${qrTab === 'ipad' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-500 hover:text-slate-700'}`}
                          >
                            Consent.
                          </button>
                        </div>
                        
                        <div className="bg-slate-50 p-1.5 border border-slate-100 rounded-lg">
                          {pairingUrls.error ? (
                            <div className="w-[100px] h-[100px] flex flex-col items-center justify-center p-1 text-center">
                              <p className="text-[9px] text-slate-500 font-bold mb-1">Error</p>
                              <button 
                                onClick={() => setPairingUrls({})}
                                className="text-[9px] bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-sm font-bold"
                              >
                                Reintentar
                              </button>
                            </div>
                          ) : (qrTab === 'mobile' ? pairingUrls.mobile : pairingUrls.ipad) ? (
                            <QRCodeCanvas value={qrTab === 'mobile' ? pairingUrls.mobile : pairingUrls.ipad} size={100} level={"L"} />
                          ) : (
                            <div className="w-[100px] h-[100px] flex items-center justify-center text-[10px] text-slate-400 text-center leading-tight">Generando...</div>
                          )}
                        </div>
                        <p className="text-[8px] font-semibold text-slate-400 text-center mt-1.5 uppercase tracking-wider">
                          {qrTab === 'mobile' ? 'Digitalizador' : 'Kiosko Paciente'}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Logout Button */}
                <button 
                  onClick={() => { logout(); }}
                  className="w-full px-3 py-1.5 mt-1 text-center text-[10px] font-bold text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-700 rounded-lg flex items-center justify-center gap-1.5 transition-colors"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  Cerrar Sesión
                </button>
              </div>
            )}
          </div>
        </aside>

        {/* ÁREA PRINCIPAL DE CONTENIDO DEL MÓDULO */}
        <main className="flex-1 flex flex-col overflow-hidden relative bg-slate-50 min-h-0 min-w-0">
          <div className="flex-1 flex flex-col h-full overflow-auto custom-scrollbar relative">
            {children}
          </div>
        </main>
      </div>

      {/* NOTIFICACIONES FLOTANTES ELEGANTES DE TICKETS */}
      <FloatingTicketNotifications />

      {/* BARRA DE ESTADO INFERIOR PEQUEÑÍSIMA (h-8 PERMANENTE) */}
      <footer className="h-8 bg-white border-t border-slate-200 px-6 flex items-center justify-between shrink-0 shadow-sm text-[11px] font-bold text-slate-600 z-40 relative overflow-hidden">
        <div className="flex items-center gap-6 overflow-hidden">
          
          {/* Progreso del Día */}
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-slate-400 uppercase tracking-wider text-[10px]">Progreso:</span>
            <span className="text-emerald-600 font-black">{globalStats?.progressPct || 0}%</span>
            <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden hidden sm:block">
              <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${globalStats?.progressPct || 0}%` }}></div>
            </div>
          </div>

          <div className="h-3 w-px bg-slate-200"></div>

          {/* Cifras de Agendados */}
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-slate-400 uppercase tracking-wider text-[10px]">Agendado:</span>
            <span className="text-slate-800 font-extrabold">{globalStats?.agendados || 0}</span>
          </div>

          {/* Cifras de Atendidos */}
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-slate-400 uppercase tracking-wider text-[10px]">Atendidos:</span>
            <span className="text-indigo-600 font-extrabold">{globalStats?.atendidos || 0}</span>
          </div>
          
        </div>

        {/* Cifra de Total Efectivo (Solo admin/finance) */}
        {user?.roles?.some(r => ['admin', 'finance', 'recepcion'].includes(r)) && (
          <div className="flex items-center gap-1.5 shrink-0 pl-4">
            <span className="text-slate-400 uppercase tracking-wider text-[10px]">Total Caja:</span>
            <span className="text-emerald-700 font-black bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-100">
              {formatCurrency(globalStats?.totalIngresos)}
            </span>
          </div>
        )}
      </footer>

      {/* LAUNCHER MODAL / COMMAND CENTER */}
      {megaMenuOpen && createPortal(
        <div 
          className="fixed inset-0 z-[300] bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 sm:p-6 md:p-8 animate-in fade-in duration-150"
          onClick={closeModuleLauncher}
        >
          <div 
            className="bg-white w-full max-w-5xl max-h-[86vh] rounded-2xl shadow-2xl border border-slate-200/80 flex flex-col overflow-hidden animate-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            {/* HEADER */}
            <div className="p-4 sm:p-5 border-b border-slate-100 bg-white sticky top-0 z-10 flex flex-col gap-3.5 shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-600 to-violet-600 text-white flex items-center justify-center shadow-md shadow-indigo-600/20 shrink-0">
                    <FolderKanban className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-black text-slate-900 tracking-tight">
                        Módulos de GEMA ERP
                      </h2>
                      <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                        Launcher
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 font-medium mt-0.5">
                      Selecciona un módulo o salta directamente a una de sus vistas principales
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 shrink-0">
                  <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-bold text-slate-400 bg-slate-100 border border-slate-200 px-2 py-1 rounded-lg">
                    <kbd className="font-mono text-[10px]">ESC</kbd> cerrar
                  </span>
                  <button 
                    type="button"
                    onClick={closeModuleLauncher}
                    className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
                    aria-label="Cerrar selector de módulos"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* INPUT DE BÚSQUEDA */}
              <div className="relative w-full">
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={menuSearchQuery}
                  onChange={(e) => setMenuSearchQuery(e.target.value)}
                  placeholder="Buscar módulo, vista o acción (ej: Corte, Citas, Empleados, Flujo de Caja)..."
                  className="w-full bg-slate-50/80 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl pl-10 pr-9 py-2.5 text-xs font-semibold text-slate-800 placeholder-slate-400 outline-none focus:border-indigo-500 focus:ring-3 focus:ring-indigo-500/15 transition-all shadow-2xs"
                />
                {menuSearchQuery && (
                  <button
                    type="button"
                    onClick={() => setMenuSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs font-bold p-0.5 rounded-full hover:bg-slate-200 cursor-pointer"
                    aria-label="Limpiar búsqueda"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* PESTAÑAS DE CATEGORÍAS */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 custom-scrollbar text-xs">
                <button
                  type="button"
                  onClick={() => setSelectedCategoryFilter('all')}
                  className={`px-3 py-1.5 rounded-lg font-bold text-xs whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer ${
                    selectedCategoryFilter === 'all'
                      ? 'bg-slate-900 text-white shadow-xs'
                      : 'bg-slate-100 hover:bg-slate-200/80 text-slate-600'
                  }`}
                >
                  <span>Todos los Módulos</span>
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                    selectedCategoryFilter === 'all' ? 'bg-slate-700 text-slate-200' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {accessibleModules.length}
                  </span>
                </button>
                {categoryTabs.map(cat => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setSelectedCategoryFilter(cat.id)}
                    className={`px-3 py-1.5 rounded-lg font-bold text-xs whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer ${
                      selectedCategoryFilter === cat.id
                        ? 'bg-indigo-600 text-white shadow-xs'
                        : 'bg-slate-100 hover:bg-slate-200/80 text-slate-600'
                    }`}
                  >
                    <span>{cat.shortLabel || cat.label}</span>
                    <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                      selectedCategoryFilter === cat.id ? 'bg-indigo-700 text-indigo-100' : 'bg-slate-200 text-slate-600'
                    }`}>
                      {cat.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* BODY CON SCROLL: TARJETAS DE MÓDULOS */}
            <div className="p-4 sm:p-5 overflow-y-auto flex-1 custom-scrollbar bg-slate-50/50">
              {displayedModules.length === 0 ? (
                <div className="text-center py-16 px-4">
                  <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-3">
                    <Search className="w-6 h-6" />
                  </div>
                  <p className="text-sm font-extrabold text-slate-700">No se encontraron resultados</p>
                  <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                    No hay módulos ni vistas que coincidan con "{menuSearchQuery}". Intenta con otros términos o cambia de categoría.
                  </p>
                  <div className="flex items-center justify-center gap-2 mt-4">
                    {menuSearchQuery && (
                      <button
                        type="button"
                        onClick={() => setMenuSearchQuery('')}
                        className="px-3 py-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors cursor-pointer"
                      >
                        Limpiar búsqueda
                      </button>
                    )}
                    {selectedCategoryFilter !== 'all' && (
                      <button
                        type="button"
                        onClick={() => setSelectedCategoryFilter('all')}
                        className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-800 bg-slate-200 hover:bg-slate-300 rounded-lg transition-colors cursor-pointer"
                      >
                        Ver todas las categorías
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {displayedModules.map((mod, idx) => {
                    const isActiveModule = currentModuleObj?.moduleId === mod.moduleId;
                    const theme = MODULE_THEMES[mod.moduleId] || {
                      bg: 'bg-indigo-50/70',
                      iconBg: 'bg-indigo-100 text-indigo-700',
                      badge: 'bg-indigo-50 text-indigo-700 border-indigo-200',
                      border: 'border-slate-200/80',
                      hoverBorder: 'hover:border-indigo-300',
                      accentText: 'text-indigo-600',
                    };

                    return (
                      <div
                        key={idx}
                        className={`flex flex-col bg-white border rounded-2xl overflow-hidden transition-all duration-200 ${
                          isActiveModule 
                            ? 'border-indigo-400 shadow-md ring-1 ring-indigo-400/20' 
                            : `border-slate-200/80 ${theme.hoverBorder} hover:shadow-md`
                        }`}
                      >
                        {/* CABECERA PRINCIPAL DEL MÓDULO */}
                        <div className="flex items-start justify-between gap-3 p-3.5 bg-slate-50/70 border-b border-slate-100">
                          <button
                            type="button"
                            onClick={(e) => handleNavigateModule(e, mod.path)}
                            className="flex items-start gap-3 text-left flex-1 min-w-0 group/mod cursor-pointer"
                          >
                            <div className={`p-2.5 rounded-xl shrink-0 transition-transform group-hover/mod:scale-105 shadow-2xs ${theme.iconBg}`}>
                              {mod.icon}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-extrabold text-sm text-slate-900 group-hover/mod:text-indigo-600 transition-colors truncate">
                                  {mod.title}
                                </h4>
                              </div>
                              <p className="text-[11px] text-slate-500 line-clamp-2 mt-0.5 leading-relaxed font-normal">
                                {mod.description}
                              </p>
                            </div>
                          </button>
                          {isActiveModule ? (
                            <span className="shrink-0 px-2 py-0.5 text-[10px] font-black bg-emerald-500 text-white rounded-full flex items-center gap-1 shadow-xs">
                              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                              Actual
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => handleNavigateModule(e, mod.path)}
                              className="shrink-0 text-slate-400 hover:text-indigo-600 p-1.5 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                              title="Ir al módulo"
                            >
                              <ArrowRight className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        {/* VISTAS Y ACCESOS DIRECTOS */}
                        {Array.isArray(mod.filteredSubOptions) && mod.filteredSubOptions.length > 0 ? (
                          <div className="p-3 bg-white flex-1 flex flex-col justify-between">
                            <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-2 flex items-center justify-between">
                              <span>Vistas y accesos directos</span>
                              <span className="font-semibold text-slate-400">{mod.filteredSubOptions.length}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-1.5">
                              {mod.filteredSubOptions.map((sub, sidx) => {
                                const isMatch = menuSearchQuery && sub.label.toLowerCase().includes(menuSearchQuery.toLowerCase());
                                return (
                                  <button
                                    key={sidx}
                                    type="button"
                                    onClick={(e) => handleNavigateSub(e, mod.path, sub)}
                                    className={`text-[11px] font-bold rounded-lg px-2.5 py-1.5 flex items-center gap-2 transition-all text-left truncate group/sub cursor-pointer ${
                                      isMatch 
                                        ? 'bg-indigo-600 text-white shadow-xs font-black' 
                                        : 'text-slate-600 hover:text-indigo-700 bg-slate-50 hover:bg-indigo-50/70 border border-slate-100 hover:border-indigo-200'
                                    }`}
                                  >
                                    <span className={`shrink-0 transition-colors ${isMatch ? 'text-white' : 'text-slate-400 group-hover/sub:text-indigo-600'}`}>
                                      {sub.icon || <ChevronRight className="w-3 h-3" />}
                                    </span>
                                    <span className="truncate flex-1">{sub.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className="p-3 bg-white flex-1 flex items-center justify-between">
                            <span className="text-[11px] text-slate-400 font-medium">Acceso directo al panel principal</span>
                            <button
                              type="button"
                              onClick={(e) => handleNavigateModule(e, mod.path)}
                              className="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50/70 hover:bg-indigo-100 transition-colors cursor-pointer"
                            >
                              <span>Abrir módulo</span>
                              <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* FOOTER DEL LAUNCHER */}
            <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 font-medium shrink-0">
              <div className="flex items-center gap-4 text-[11px]">
                <span className="flex items-center gap-1.5">
                  <kbd className="px-1.5 py-0.5 rounded bg-white border border-slate-200 font-mono text-[10px] text-slate-600 shadow-2xs">ESC</kbd> Cerrar
                </span>
                <span className="hidden sm:flex items-center gap-1.5">
                  <kbd className="px-1.5 py-0.5 rounded bg-white border border-slate-200 font-mono text-[10px] text-slate-600 shadow-2xs">Cmd/Ctrl + Clic</kbd> Nueva pestaña
                </span>
              </div>
              <div className="text-[11px] font-semibold text-slate-400">
                {displayedModules.length} {displayedModules.length === 1 ? 'módulo visible' : 'módulos visibles'}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* RICH TOOLTIP FLOTANTE (PORTAL) */}
      {activeTooltip && createPortal(
        <div
          style={{
            position: 'fixed',
            left: `${activeTooltip.x}px`,
            top: `${activeTooltip.y}px`,
            transform: 'translateY(-50%)',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
          className="flex items-center animate-in fade-in zoom-in-95 duration-100"
        >
          {/* Flecha apuntando a la izquierda */}
          <div className="w-0 h-0 border-y-[5px] border-y-transparent border-r-[6px] border-r-slate-900 drop-shadow-xs" />
          
          <div className="bg-slate-900/95 backdrop-blur-md text-white text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-700/80 shadow-2xl flex flex-col gap-0.5 whitespace-nowrap min-w-max">
            <div className="flex items-center gap-2">
              <span className="font-bold text-slate-100">{activeTooltip.title}</span>
              {activeTooltip.badge && (
                <span className="px-1.5 py-0.2 rounded text-[9px] font-black uppercase tracking-wider bg-indigo-500/30 text-indigo-300 border border-indigo-400/30">
                  {activeTooltip.badge}
                </span>
              )}
            </div>
            {activeTooltip.subtitle && (
              <span className="text-[10px] font-medium text-slate-400">
                {activeTooltip.subtitle}
              </span>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}